<template>
  <!-- Auth gate -->
  <div v-if="!isAdmin" class="login-container">
    <el-card class="login-card">
      <h2>Admin access required</h2>
      <p style="text-align:center;color:#555;margin-bottom:1.5rem">
        You need to be logged in as an admin to manage users.
      </p>
      <router-link to="/manage-cards">
        <button type="button"><i class="fas fa-arrow-left"></i> Go to admin login</button>
      </router-link>
    </el-card>
  </div>

  <div v-else id="game-users-page" class="admin-page">
    <AdminTabs />

    <!-- ── Page header ────────────────────────────────────── -->
    <div class="admin-page-header">
      <h1>Manage Users</h1>
      <button type="button" class="admin-btn admin-btn--primary" @click="openAddModal">
        <i class="fas fa-user-plus"></i> Add New User
      </button>
    </div>

    <!-- ── Add User modal ─────────────────────────────────── -->
    <SettingsModal :show="showAddModal" @update:show="showAddModal = $event">
      <template #header>
        <h3>Add User</h3>
      </template>

      <template #body>
        <div class="admin-form-row">
          <div class="admin-form-group">
            <label class="admin-label" for="new-email">Email address</label>
            <input
              id="new-email"
              v-model="newUser.email"
              type="email"
              class="admin-input"
              placeholder="name@workplace.com"
              autocomplete="off"
            />
          </div>
          <div class="admin-form-group">
            <label class="admin-label" for="new-password">Starting password</label>
            <input
              id="new-password"
              v-model="newUser.password"
              type="password"
              class="admin-input"
              placeholder="At least 6 characters"
              autocomplete="new-password"
            />
          </div>
        </div>

        <div class="admin-form-group">
          <label class="admin-label">Department access</label>
          <p class="admin-hint">
            Add a row for each department. <strong>Full</strong> = all cards &nbsp;|&nbsp; <strong>Demo only</strong> = demo-flagged cards only.
          </p>

          <div class="admin-add-access-list">
            <div
              v-for="(row, idx) in newUser.access"
              :key="'na-' + idx"
              class="admin-add-access-item"
            >
              <select v-model="row.department_id" class="admin-input admin-input--select">
                <option value="">— Select department —</option>
                <option
                  v-for="dept in availableDepartmentsForNew(idx)"
                  :key="dept.id"
                  :value="dept.id"
                >{{ dept.name }}</option>
              </select>
              <select v-model="row.access_mode" class="admin-input admin-input--select admin-input--mode">
                <option value="full">Full access</option>
                <option value="demo_only">Demo only</option>
              </select>
              <button
                type="button"
                class="admin-icon-btn admin-icon-btn--delete"
                title="Remove row"
                @click="newUser.access.splice(idx, 1)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>

            <button
              type="button"
              class="admin-add-row-btn"
              :disabled="newUser.access.length >= departments.length"
              @click="newUser.access.push({ department_id: '', access_mode: 'full' })"
            >
              <i class="fas fa-plus"></i> Add Department
            </button>
          </div>
        </div>

        <div v-if="addError" class="admin-msg admin-msg--error">{{ addError }}</div>
        <div v-if="addSuccess" class="admin-msg admin-msg--success">{{ addSuccess }}</div>
      </template>

      <template #footer>
        <button
          type="button"
          class="admin-btn admin-btn--primary"
          @click="createUser"
          :disabled="!newUser.email || !newUser.password || newUser.access.length === 0 || addLoading"
        >
          <i class="fas fa-user-plus"></i>
          {{ addLoading ? 'Creating…' : 'Create User' }}
        </button>
      </template>
    </SettingsModal>

    <!-- ── Edit User modal ────────────────────────────────── -->
    <SettingsModal :show="showEditModal" @update:show="showEditModal = $event">
      <template #header>
        <h3>Edit User</h3>
      </template>

      <template #body>
        <p class="admin-hint" style="margin-bottom:1rem">
          Editing access for <strong>{{ editingUser?.email }}</strong>.
          To change their password use the reset key icon in the user table.
        </p>

        <div class="admin-form-group">
          <label class="admin-label">Department access</label>
          <p class="admin-hint">
            <strong>NONE</strong> = no access &nbsp;|&nbsp;
            <strong>DEMO</strong> = demo-flagged cards only &nbsp;|&nbsp;
            <strong>FULL</strong> = all cards
          </p>
          <div v-if="departments.length" class="admin-access-matrix">
            <div
              v-for="dept in departments"
              :key="'ea-' + dept.id"
              class="admin-access-row"
            >
              <div class="admin-access-dept">{{ dept.name }}</div>
              <div class="admin-access-modes" role="radiogroup" :aria-label="`Access for ${dept.name}`">
                <label class="admin-access-choice">
                  <input
                    v-model="editAccessByDepartment[dept.id]"
                    :name="`edit-access-${dept.id}`"
                    type="radio"
                    value="none"
                  />
                  <span>NONE</span>
                </label>
                <label class="admin-access-choice">
                  <input
                    v-model="editAccessByDepartment[dept.id]"
                    :name="`edit-access-${dept.id}`"
                    type="radio"
                    value="demo_only"
                  />
                  <span>DEMO</span>
                </label>
                <label class="admin-access-choice">
                  <input
                    v-model="editAccessByDepartment[dept.id]"
                    :name="`edit-access-${dept.id}`"
                    type="radio"
                    value="full"
                  />
                  <span>FULL</span>
                </label>
              </div>
            </div>
          </div>
          <div v-else class="admin-hint">No departments found.</div>
        </div>

        <div v-if="editError" class="admin-msg admin-msg--error">{{ editError }}</div>
      </template>

      <template #footer>
        <button type="button" class="admin-btn admin-btn--primary" @click="saveEdit" :disabled="editLoading">
          <i class="fas fa-save"></i>
          {{ editLoading ? 'Saving…' : 'Save Changes' }}
        </button>
      </template>
    </SettingsModal>

    <!-- ── Users table ────────────────────────────────────── -->
    <div class="admin-table-wrap">
      <div v-if="listLoading" class="admin-state">
        <i class="fas fa-circle-notch fa-spin" style="margin-right:0.5rem"></i>Loading users…
      </div>
      <div v-else-if="users.length === 0" class="admin-state">
        No users yet — click <strong>Add New User</strong> to get started.
      </div>

      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Status</th>
            <th>Department access</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="u in users"
            :key="u.id"
            :class="{ 'is-disabled': u.status !== 'active' }"
          >
            <td class="col-email">{{ u.email }}</td>

            <td>
              <span :class="['admin-badge', u.status === 'active' ? 'admin-badge--active' : 'admin-badge--disabled']">
                {{ u.status === 'active' ? 'Active' : 'Disabled' }}
              </span>
            </td>

            <td class="col-depts">
              <div class="admin-pill-list">
                <template v-if="u.access.length">
                  <span
                    v-for="a in u.access"
                    :key="u.id + '-' + a.department_id"
                    class="admin-pill"
                    :class="'admin-pill--' + a.access_mode"
                  >
                    {{ getDeptName(a.department_id) }}
                    <em>{{ a.access_mode === 'demo_only' ? 'demo' : 'full' }}</em>
                  </span>
                </template>
                <span v-else class="admin-pill-none">No access</span>
              </div>
            </td>

            <td class="col-actions">
              <div class="admin-icon-btn-group">
                <el-tooltip content="Edit user" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                  <button
                    type="button"
                    class="admin-icon-btn"
                    @click="openEditModal(u)"
                  >
                    <i class="fas fa-pencil-alt"></i>
                  </button>
                </el-tooltip>

                <el-tooltip :content="u.status === 'active' ? 'Disable user' : 'Enable user'" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                  <button
                    type="button"
                    :class="['admin-icon-btn', u.status === 'active' ? 'admin-icon-btn--warn' : '']"
                    @click="toggleUserStatus(u)"
                  >
                    <i :class="u.status === 'active' ? 'fas fa-ban' : 'fas fa-check-circle'"></i>
                  </button>
                </el-tooltip>

                <el-tooltip :content="'Send password reset email to ' + u.email" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                  <button
                    type="button"
                    class="admin-icon-btn"
                    :disabled="resetState[u.id] === 'loading' || resetState[u.id] === 'sent'"
                    @click="sendReset(u)"
                  >
                    <i :class="{
                      'fas fa-key':                   !resetState[u.id] || resetState[u.id] === 'idle',
                      'fas fa-spinner fa-spin':         resetState[u.id] === 'loading',
                      'fas fa-check':                   resetState[u.id] === 'sent',
                      'fas fa-exclamation-triangle':    resetState[u.id] === 'error',
                    }"></i>
                  </button>
                </el-tooltip>

                <el-tooltip content="Delete user" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                  <button
                    type="button"
                    class="admin-icon-btn admin-icon-btn--delete"
                    @click="deleteUser(u)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </el-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="admin-table-footer">
        <button type="button" class="admin-add-row-btn" @click="openAddModal">
          <i class="fas fa-plus"></i> Add User
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AdminTabs from '@/components/AdminTabs.vue'
import SettingsModal from '@/components/SettingsModal.vue'
import { supabase } from '@/supabase.js'
import { ElMessage } from 'element-plus'

// ── Auth ─────────────────────────────────────────────────────
const isAdmin = ref(false)
onMounted(async () => {
  if (localStorage.getItem('loggedIn') === 'true' && localStorage.getItem('adminUser')) {
    isAdmin.value = true
    await Promise.all([loadDepartments(), loadUsers()])
  }
})

// ── Departments ───────────────────────────────────────────────
const departments = ref([])
const loadDepartments = async () => {
  const { data, error } = await supabase.from('departments').select('*').order('position')
  if (!error) departments.value = data || []
}

const getDeptName = (id) => {
  const d = departments.value.find(d => d.id === id)
  return d ? d.name : id
}

// ── Users list ────────────────────────────────────────────────
const users = ref([])
const listLoading = ref(false)
const loadUsers = async () => {
  listLoading.value = true
  try {
    const { data, error } = await supabase.rpc('list_game_users')
    if (error) throw error
    users.value = (data || []).map(u => ({
      id: u.id,
      email: u.email,
      status: u.status,
      access: Array.isArray(u.access) ? u.access : JSON.parse(u.access || '[]'),
    }))
  } catch (e) {
    console.error('Error loading users:', e)
    ElMessage.error('Could not load users.')
  } finally {
    listLoading.value = false
  }
}

// ── Add user modal ────────────────────────────────────────────
const showAddModal = ref(false)
const addLoading = ref(false)
const addError = ref('')
const addSuccess = ref('')
const newUser = ref({ email: '', password: '', access: [] })

const resetNewUser = () => {
  newUser.value = { email: '', password: '', access: [] }
  addError.value = ''
  addSuccess.value = ''
}

const openAddModal = () => { resetNewUser(); showAddModal.value = true }

const availableDepartmentsForNew = (idx) => {
  const selected = newUser.value.access
    .map((r, i) => i !== idx ? r.department_id : null)
    .filter(Boolean)
  return departments.value.filter(d => !selected.includes(d.id))
}

const createUser = async () => {
  addError.value = ''
  addSuccess.value = ''
  if (!newUser.value.email.trim() || !newUser.value.password) {
    addError.value = 'Email and password are required.'; return
  }
  if (newUser.value.access.length === 0) {
    addError.value = 'Assign at least one department.'; return
  }
  if (newUser.value.access.some(r => !r.department_id)) {
    addError.value = 'Please select a department for each row.'; return
  }
  if (newUser.value.password.length < 6) {
    addError.value = 'Password must be at least 6 characters.'; return
  }

  addLoading.value = true
  try {
    const { data: authData, error: signUpErr } = await supabase.auth.signUp({
      email: newUser.value.email.trim(),
      password: newUser.value.password,
      options: {
        emailRedirectTo: `${window.location.origin}/game-login`,
      },
    })
    if (signUpErr) throw signUpErr
    const newAuthUser = authData?.user
    if (!newAuthUser?.id) throw new Error('User creation returned no ID — the email may already be registered.')

    await supabase.auth.signOut()

    const { error: rpcErr } = await supabase.rpc('provision_game_user', {
      p_user_id: newAuthUser.id,
      p_email: newUser.value.email.trim(),
      p_access: newUser.value.access.map(a => ({
        department_id: a.department_id,
        access_mode: a.access_mode,
      })),
    })
    if (rpcErr) throw rpcErr

    ElMessage.success(`User ${newUser.value.email} created.`)
    showAddModal.value = false
    resetNewUser()
    await loadUsers()
  } catch (e) {
    console.error('Error creating user:', e)
    addError.value = e.message || 'Failed to create user.'
  } finally {
    addLoading.value = false
  }
}

// ── Edit user modal ───────────────────────────────────────────
const showEditModal = ref(false)
const editLoading = ref(false)
const editError = ref('')
const editingUser = ref(null)
const editAccessByDepartment = ref({})

const buildEditAccessByDepartment = (accessRows = []) => {
  const mapped = {}
  for (const dept of departments.value) {
    mapped[dept.id] = 'none'
  }
  for (const row of accessRows) {
    if (!row?.department_id) continue
    if (row.access_mode !== 'full' && row.access_mode !== 'demo_only') continue
    const key = String(row.department_id)
    if (mapped[key] !== undefined) mapped[key] = row.access_mode
  }
  return mapped
}

const openEditModal = (u) => {
  editingUser.value = u
  editAccessByDepartment.value = buildEditAccessByDepartment(u.access)
  editError.value = ''
  showEditModal.value = true
}

const saveEdit = async () => {
  editError.value = ''
  const nextAccess = departments.value
    .map((dept) => ({
      department_id: dept.id,
      access_mode: editAccessByDepartment.value[dept.id] || 'none',
    }))
    .filter((row) => row.access_mode === 'full' || row.access_mode === 'demo_only')
  editLoading.value = true
  try {
    const { error } = await supabase.rpc('update_game_user_access', {
      p_user_id: editingUser.value.id,
      p_access: nextAccess,
    })
    if (error) throw error
    editingUser.value.access = nextAccess.map(a => ({ ...a }))
    ElMessage.success('Access updated.')
    showEditModal.value = false
  } catch (e) {
    console.error('Error saving access:', e)
    editError.value = e.message || 'Could not save department access.'
  } finally {
    editLoading.value = false
  }
}

// ── Enable / Disable ──────────────────────────────────────────
const toggleUserStatus = async (u) => {
  const newStatus = u.status === 'active' ? 'disabled' : 'active'
  try {
    const { error } = await supabase.rpc('set_game_user_status', {
      p_user_id: u.id,
      p_status: newStatus,
    })
    if (error) throw error
    u.status = newStatus
  } catch (e) {
    console.error('Error toggling status:', e)
    ElMessage.error('Could not update user status.')
  }
}

// ── Delete user ───────────────────────────────────────────────
const deleteUser = async (u) => {
  if (!confirm(`Delete user "${u.email}"? This cannot be undone.`)) return
  try {
    const { error } = await supabase.from('app_users').delete().eq('id', u.id)
    if (error) throw error
    users.value = users.value.filter(x => x.id !== u.id)
    ElMessage.success('User deleted.')
  } catch (e) {
    console.error('Error deleting user:', e)
    ElMessage.error('Could not delete user: ' + e.message)
  }
}

// ── Password reset ────────────────────────────────────────────
const resetState = ref({})
const sendReset = async (u) => {
  resetState.value[u.id] = 'loading'
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(u.email, {
      redirectTo: `${window.location.origin}/reset-password?next=game-login`,
    })
    if (error) throw error
    resetState.value[u.id] = 'sent'
    setTimeout(() => { resetState.value[u.id] = 'idle' }, 4000)
  } catch (e) {
    console.error('Password reset error:', e)
    resetState.value[u.id] = 'error'
    setTimeout(() => { resetState.value[u.id] = 'idle' }, 4000)
  }
}
</script>

<style lang="scss" src="@/styles/_gameUsers.scss"></style>
